import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  Shield, Lock, Eye, EyeOff, Users, Crown, User, 
  Calendar, Activity, LogOut, Loader2, AlertTriangle,
  CheckCircle, XCircle, Search, ChevronDown, ChevronUp,
  Infinity, RefreshCw, Clock, Zap, KeyRound, Smartphone,
  QrCode, Copy, Check, DollarSign, TrendingUp, TrendingDown,
  UserMinus, Receipt, PiggyBank, Ban, UserCheck, Globe,
  History, CreditCard, Mail, X, PieChart as PieChartIcon, LineChart as LineChartIcon,
  Filter, ChevronLeft, ChevronRight, Layers, CalendarDays
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Area, AreaChart,
  BarChart, Bar, ReferenceLine
} from 'recharts';
import api from '../lib/api';

// Admin password (server-side validated now)
const ADMIN_PASSWORD = 'Cadet012768!'; // Only for client-side lockout - real auth is server-side

// Create axios instance with admin token interceptor
const adminApi = {
  get: async (url, config = {}) => {
    const token = sessionStorage.getItem('adminSessionToken');
    return api.get(url, { 
      ...config, 
      headers: { ...config.headers, 'X-Admin-Token': token }
    });
  },
  post: async (url, data, config = {}) => {
    const token = sessionStorage.getItem('adminSessionToken');
    return api.post(url, data, { 
      ...config, 
      headers: { ...config.headers, 'X-Admin-Token': token }
    });
  }
};

const AdminPage = () => {
  const navigate = useNavigate();
  
  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authStep, setAuthStep] = useState('password');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  
  // MFA Setup states
  const [mfaSetupStep, setMfaSetupStep] = useState(1);
  const [setupMfaCode, setSetupMfaCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [showMfaSetupDialog, setShowMfaSetupDialog] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaTempSecret, setMfaTempSecret] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [stats, setStats] = useState({ 
    total: 0, free: 0, pro_total: 0,
    pro_monthly: 0, pro_annual: 0, pro_lifetime: 0, pro_legacy: 0,
    churned_users: 0, churn_rate: 0,
    total_revenue: 0, mrr: 0, arr: 0,
    user_distribution: []
  });
  const [mrrHistory, setMrrHistory] = useState([]);

  // Chart data states
  const [chartDateRange, setChartDateRange] = useState(365);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [churnData, setChurnData] = useState([]);
  const [chartGranularity, setChartGranularity] = useState('month');
  const [loadingCharts, setLoadingCharts] = useState(false);

  // Custom date range state
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [customRangeLabel, setCustomRangeLabel] = useState('');

  // User detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);

  // Ban dialog
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banUserId, setBanUserId] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [banning, setBanning] = useState(false);

  // Unban dialog
  const [showUnbanDialog, setShowUnbanDialog] = useState(false);
  const [unbanUserId, setUnbanUserId] = useState(null);
  const [unbanReason, setUnbanReason] = useState('');
  const [unbanning, setUnbanning] = useState(false);

  // Admin upgrade dialog
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeUserId, setUpgradeUserId] = useState(null);
  const [upgradeForm, setUpgradeForm] = useState({
    subscription_type: 'monthly',
    reason: '',
    price: 9.99,
    notes: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Advanced filtering state
  const [filters, setFilters] = useState({
    accountTypes: [], // ['free', 'pro']
    subscriptionTypes: [], // ['monthly', 'annual', 'lifetime']
    statuses: [] // ['active', 'banned', 'churned']
  });
  const [showFilterPopover, setShowFilterPopover] = useState(false);

  // Grouping state
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'account_type', 'subscription_type', 'status'

  // Lockout timer effect
  useEffect(() => {
    let timer;
    if (isLocked && lockTimer > 0) {
      timer = setInterval(() => {
        setLockTimer(prev => {
          if (prev <= 1) {
            setIsLocked(false);
            setLoginAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isLocked, lockTimer]);

  // Check session and MFA status
  useEffect(() => {
    const checkSession = async () => {
      const adminAuth = sessionStorage.getItem('adminAuth');
      
      // Check if MFA is enabled on server
      try {
        const mfaStatus = await api.get('/admin/mfa/status');
        setMfaEnabled(mfaStatus.data.enabled);
      } catch (e) {
        console.error('Failed to check MFA status:', e);
      }
      
      if (adminAuth === 'authenticated') {
        setIsAuthenticated(true);
        setAuthStep('authenticated');
        loadUsers();
        loadStats();
        
        // Check if we should prompt MFA setup (after first login, if not enabled)
        const loginCount = parseInt(localStorage.getItem('adminLoginCount') || '0', 10);
        const mfaStatus = await api.get('/admin/mfa/status').catch(() => ({ data: { enabled: false }}));
        if (loginCount > 0 && !mfaStatus.data.enabled) {
          setShowMfaSetupDialog(true);
        }
      }
    };
    checkSession();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) {
      toast.error(`Account locked. Try again in ${lockTimer} seconds.`);
      return;
    }
    setIsLoading(true);
    
    try {
      // Server-side password validation
      const response = await api.post('/admin/auth/password', { password });
      
      if (response.data.session_token) {
        // Store session token for subsequent requests
        sessionStorage.setItem('adminSessionToken', response.data.session_token);
        
        // Check if MFA is required
        const mfaStatus = await api.get('/admin/mfa/status');
        if (mfaStatus.data.enabled) {
          setAuthStep('mfa');
          toast.success('Password verified. Enter your authenticator code.');
        } else {
          // Complete MFA step without code (server will mark session as verified)
          await api.post('/admin/auth/mfa', { 
            session_token: response.data.session_token, 
            code: '' 
          });
          completeAuthentication();
        }
      }
    } catch (error) {
      const attempts = loginAttempts + 1;
      setLoginAttempts(attempts);
      if (attempts >= 3) {
        setIsLocked(true);
        setLockTimer(30);
        toast.error('Too many failed attempts. Locked for 30 seconds.');
      } else {
        toast.error(`Invalid password. ${3 - attempts} attempts remaining.`);
      }
    }
    setIsLoading(false);
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const sessionToken = sessionStorage.getItem('adminSessionToken');
      const response = await api.post('/admin/auth/mfa', { 
        session_token: sessionToken, 
        code: mfaCode 
      });
      if (response.data.success) {
        completeAuthentication();
      } else {
        toast.error('Invalid MFA code. Please try again.');
      }
    } catch (e) {
      toast.error('Invalid MFA code. Please try again.');
    }
    setIsLoading(false);
  };

  const completeAuthentication = async () => {
    setIsAuthenticated(true);
    setAuthStep('authenticated');
    sessionStorage.setItem('adminAuth', 'authenticated');
    const loginCount = parseInt(localStorage.getItem('adminLoginCount') || '0', 10);
    localStorage.setItem('adminLoginCount', String(loginCount + 1));
    toast.success('Welcome to Admin Panel');
    loadUsers();
    loadStats();
    
    // Check if MFA is enabled, if not prompt setup after first login
    try {
      const mfaStatus = await api.get('/admin/mfa/status');
      if (loginCount > 0 && !mfaStatus.data.enabled) {
        setTimeout(() => setShowMfaSetupDialog(true), 1000);
      }
    } catch (e) {
      console.error('Failed to check MFA status:', e);
    }
  };

  const handleMfaSetup = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/admin/mfa/setup');
      setMfaQrCode(response.data.qr_code);
      setMfaTempSecret(response.data.secret);
      setMfaSetupStep(2);
    } catch (e) {
      toast.error('Failed to generate MFA secret');
    }
    setIsLoading(false);
  };

  const handleMfaSetupVerify = async () => {
    if (setupMfaCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setIsLoading(true);
    try {
      // Verify the code with the temp secret
      const verifyResponse = await api.post('/admin/mfa/verify', null, { 
        params: { code: setupMfaCode, secret: mfaTempSecret } 
      });
      
      if (verifyResponse.data.valid) {
        // Save the secret to enable MFA
        await api.post('/admin/mfa/save', null, { params: { secret: mfaTempSecret } });
        setMfaEnabled(true);
        setShowMfaSetupDialog(false);
        setMfaSetupStep(1);
        setSetupMfaCode('');
        toast.success('MFA has been set up successfully! You will need to enter a code on next login.');
      } else {
        toast.error('Invalid code. Please check your authenticator app and try again.');
      }
    } catch (e) {
      toast.error('Failed to verify MFA code');
    }
    setIsLoading(false);
  };

  const handleDisableMfa = async () => {
    try {
      await api.post('/admin/mfa/disable');
      setMfaEnabled(false);
      toast.success('MFA has been disabled');
    } catch (e) {
      toast.error('Failed to disable MFA');
    }
  };

  const skipMfaSetup = () => {
    setShowMfaSetupDialog(false);
    setMfaSetupStep(1);
    toast.info('MFA setup skipped. You can enable it later from settings.');
  };

  const copySecret = () => {
    navigator.clipboard.writeText(mfaTempSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setAuthStep('password');
    setPassword('');
    setMfaCode('');
    setUsers([]);
    toast.success('Logged out of admin panel');
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await adminApi.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      if (error.response?.status === 401) {
        // Session expired
        sessionStorage.removeItem('adminAuth');
        sessionStorage.removeItem('adminSessionToken');
        setIsAuthenticated(false);
        setAuthStep('password');
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadStats = async () => {
    try {
      const [statsRes, mrrRes] = await Promise.all([
        adminApi.get('/admin/stats'),
        adminApi.get('/admin/mrr-history')
      ]);
      setStats(statsRes.data);
      setMrrHistory(mrrRes.data.mrr_history || []);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadChartData = async (days = chartDateRange) => {
    setLoadingCharts(true);
    try {
      const [growthRes, churnRes] = await Promise.all([
        adminApi.get(`/admin/user-growth?days=${days}`),
        adminApi.get(`/admin/churn-data?days=${days}`)
      ]);
      setUserGrowthData(growthRes.data.growth_data || []);
      setChurnData(churnRes.data.churn_data || []);
      setChartGranularity(growthRes.data.granularity || 'month');
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setLoadingCharts(false);
    }
  };

  // Load chart data when date range changes
  useEffect(() => {
    if (isAuthenticated) {
      loadChartData(chartDateRange);
    }
  }, [chartDateRange, isAuthenticated]);

  const loadUserDetail = async (userId) => {
    setUserDetailLoading(true);
    setShowUserDetail(true);
    try {
      const response = await adminApi.get(`/admin/user/${userId}`);
      setSelectedUser(response.data.user);
    } catch (error) {
      toast.error('Failed to load user details');
      setShowUserDetail(false);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleBanUser = async () => {
    if (!banUserId) return;
    setBanning(true);
    try {
      await adminApi.post(`/admin/user/${banUserId}/ban`, { reason: banReason });
      toast.success('User has been banned');
      setShowBanDialog(false);
      setBanUserId(null);
      setBanReason('');
      loadUsers();
    } catch (error) {
      toast.error('Failed to ban user');
    } finally {
      setBanning(false);
    }
  };

  const handleUnbanUser = async () => {
    if (!unbanUserId || !unbanReason.trim()) {
      toast.error('Please provide a reason for unbanning');
      return;
    }
    setUnbanning(true);
    try {
      await adminApi.post(`/admin/user/${unbanUserId}/unban`, { reason: unbanReason });
      toast.success('User has been unbanned');
      setShowUnbanDialog(false);
      setUnbanUserId(null);
      setUnbanReason('');
      loadUsers();
      if (selectedUser?.id === unbanUserId) {
        loadUserDetail(unbanUserId);
      }
    } catch (error) {
      toast.error('Failed to unban user');
    } finally {
      setUnbanning(false);
    }
  };

  const openUnbanDialog = (userId) => {
    setUnbanUserId(userId);
    setUnbanReason('');
    setShowUnbanDialog(true);
  };

  const handleAdminUpgrade = async () => {
    if (!upgradeUserId) return;
    if (!upgradeForm.reason.trim()) {
      toast.error('Please provide a reason for the upgrade');
      return;
    }
    // If reason is "other", notes are required
    if (upgradeForm.reason === 'other' && !upgradeForm.notes.trim()) {
      toast.error('Please provide notes when selecting "Other" as the reason');
      return;
    }
    if (upgradeForm.price < 0) {
      toast.error('Price cannot be negative');
      return;
    }
    setIsLoading(true);
    try {
      // Combine reason with notes if "other" is selected
      const finalReason = upgradeForm.reason === 'other' 
        ? `other: ${upgradeForm.notes}` 
        : upgradeForm.reason;
      
      await adminApi.post(`/admin/user/${upgradeUserId}/upgrade`, {
        subscription_type: upgradeForm.subscription_type,
        reason: finalReason,
        price: parseFloat(upgradeForm.price)
      });
      toast.success(`User upgraded to ${upgradeForm.subscription_type} Pro`);
      setShowUpgradeDialog(false);
      setUpgradeUserId(null);
      setUpgradeForm({ subscription_type: 'monthly', reason: '', price: 9.99, notes: '' });
      loadUsers();
      loadStats();
      if (selectedUser?.id === upgradeUserId) {
        loadUserDetail(upgradeUserId);
      }
    } catch (error) {
      toast.error('Failed to upgrade user');
    } finally {
      setIsLoading(false);
    }
  };

  const openUpgradeDialog = (userId) => {
    setUpgradeUserId(userId);
    setUpgradeForm({ subscription_type: 'monthly', reason: '', price: 9.99, notes: '' });
    setShowUpgradeDialog(true);
  };

  const openBanDialog = (userId, e) => {
    e.stopPropagation();
    setBanUserId(userId);
    setShowBanDialog(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  // Toggle filter value
  const toggleFilter = (category, value) => {
    setFilters(prev => {
      const current = prev[category];
      const updated = current.includes(value) 
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      accountTypes: [],
      subscriptionTypes: [],
      statuses: []
    });
    setCurrentPage(1);
  };

  // Get active filter count
  const activeFilterCount = filters.accountTypes.length + filters.subscriptionTypes.length + filters.statuses.length;

  // Get user status
  const getUserStatus = (user) => {
    if (user.banned) return 'banned';
    if (user.churned_at) return 'churned';
    return 'active';
  };

  // Apply filters and search
  const filteredUsers = useMemo(() => {
    return users
      .filter(user => {
        // Search filter
        const searchMatch = !searchTerm || 
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Account type filter
        const accountTypeMatch = filters.accountTypes.length === 0 || 
          filters.accountTypes.includes(user.account_type);
        
        // Subscription type filter
        const subType = user.subscription_type || (user.account_type === 'free' ? 'free' : 'legacy');
        const subscriptionMatch = filters.subscriptionTypes.length === 0 || 
          filters.subscriptionTypes.includes(subType);
        
        // Status filter
        const status = getUserStatus(user);
        const statusMatch = filters.statuses.length === 0 || 
          filters.statuses.includes(status);
        
        return searchMatch && accountTypeMatch && subscriptionMatch && statusMatch;
      })
      .sort((a, b) => {
        let aVal = a[sortField] || '';
        let bVal = b[sortField] || '';
        if (['created_at', 'last_login', 'upgraded_at', 'churned_at', 'total_revenue'].includes(sortField)) {
          aVal = sortField === 'total_revenue' ? (aVal || 0) : new Date(aVal || 0).getTime();
          bVal = sortField === 'total_revenue' ? (bVal || 0) : new Date(bVal || 0).getTime();
        }
        return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });
  }, [users, searchTerm, filters, sortField, sortDirection]);

  // Group users if grouping is enabled
  const groupedUsers = useMemo(() => {
    if (groupBy === 'none') return null;
    
    const groups = {};
    filteredUsers.forEach(user => {
      let key;
      if (groupBy === 'account_type') {
        key = user.account_type === 'pro' ? 'Pro' : 'Free';
      } else if (groupBy === 'subscription_type') {
        if (user.account_type === 'free') key = 'Free';
        else key = user.subscription_type ? user.subscription_type.charAt(0).toUpperCase() + user.subscription_type.slice(1) : 'Legacy';
      } else if (groupBy === 'status') {
        key = getUserStatus(user).charAt(0).toUpperCase() + getUserStatus(user).slice(1);
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(user);
    });
    return groups;
  }, [filteredUsers, groupBy]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getSubscriptionBadge = (user) => {
    if (user.banned) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <Ban className="h-3 w-3" /> Banned
        </span>
      );
    }
    if (user.account_type !== 'pro') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          <User className="h-3 w-3" /> Free
        </span>
      );
    }
    const subType = user.subscription_type || 'legacy';
    switch (subType) {
      case 'lifetime':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border border-amber-200">
            <Infinity className="h-3 w-3" /> Lifetime
          </span>
        );
      case 'annual':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            <Crown className="h-3 w-3" /> Annual
          </span>
        );
      case 'monthly':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            <Crown className="h-3 w-3" /> Monthly
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            <Crown className="h-3 w-3" /> Pro
          </span>
        );
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  // Password Step
  if (authStep === 'password') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl p-8 bg-white/95 backdrop-blur-xl shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
            <p className="text-gray-500 text-sm mt-1">Enter admin credentials to continue</p>
          </div>
          {isLocked && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-sm font-medium text-red-800">Account Locked</p>
                <p className="text-xs text-red-600">Try again in {lockTimer} seconds</p>
              </div>
            </div>
          )}
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Admin Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLocked}
                  className="h-12 rounded-xl pr-10"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full h-12 rounded-full bg-gray-900 hover:bg-gray-800" disabled={isLoading || isLocked || !password}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              {isLoading ? 'Verifying...' : 'Continue'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">← Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  // MFA Step
  if (authStep === 'mfa') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl p-8 bg-white/95 backdrop-blur-xl shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h1>
            <p className="text-gray-500 text-sm mt-1">Enter the 6-digit code from your authenticator app</p>
          </div>
          <form onSubmit={handleMfaSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>MFA Code</Label>
              <Input type="text" placeholder="000000" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))} className="h-14 rounded-xl text-center text-2xl tracking-widest font-mono" />
            </div>
            <Button type="submit" className="w-full h-12 rounded-full bg-gray-900 hover:bg-gray-800" disabled={isLoading || mfaCode.length !== 6}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              {isLoading ? 'Verifying...' : 'Verify & Login'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => setAuthStep('password')} className="text-sm text-gray-500 hover:text-gray-700">← Back to Password</button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* MFA Setup Dialog */}
      <Dialog open={showMfaSetupDialog} onOpenChange={(open) => { if (!open) { setMfaSetupStep(1); setSetupMfaCode(''); } setShowMfaSetupDialog(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-gray-600" /> Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>Secure your admin account with MFA</DialogDescription>
          </DialogHeader>
          {mfaSetupStep === 1 && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Recommended Security Step</p>
                    <p className="text-xs text-amber-700 mt-1">Enable two-factor authentication to add an extra layer of security.</p>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-2">
                <p>You'll need an authenticator app like:</p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-gray-500">
                  <li>Google Authenticator</li>
                  <li>Authy</li>
                  <li>Microsoft Authenticator</li>
                  <li>1Password</li>
                </ul>
              </div>
              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button variant="outline" onClick={skipMfaSetup}>Skip for Now</Button>
                <Button onClick={handleMfaSetup} disabled={isLoading} className="bg-gray-900 hover:bg-gray-800">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Smartphone className="h-4 w-4 mr-2" />}
                  Set Up MFA
                </Button>
              </DialogFooter>
            </div>
          )}
          {mfaSetupStep === 2 && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">Scan this QR code with your authenticator app:</p>
                {mfaQrCode ? (
                  <img src={mfaQrCode} alt="MFA QR Code" className="w-48 h-48 mx-auto rounded-xl border border-gray-200" />
                ) : (
                  <div className="w-48 h-48 mx-auto bg-gray-100 rounded-xl flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-3 mb-2">Or enter this secret manually:</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="bg-gray-100 px-3 py-2 rounded-lg font-mono text-xs break-all max-w-[200px]">{mfaTempSecret}</code>
                  <Button variant="ghost" size="sm" onClick={copySecret}>{secretCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Enter the 6-digit code from your app</Label>
                <Input type="text" placeholder="000000" maxLength={6} value={setupMfaCode} onChange={(e) => setSetupMfaCode(e.target.value.replace(/\D/g, ''))} className="h-12 text-center text-xl tracking-widest font-mono" />
              </div>
              <DialogFooter className="flex flex-col gap-2">
                <Button onClick={handleMfaSetupVerify} disabled={setupMfaCode.length !== 6 || isLoading} className="w-full bg-gray-900 hover:bg-gray-800">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  Verify & Enable MFA
                </Button>
                <Button variant="ghost" onClick={() => { setMfaSetupStep(1); setSetupMfaCode(''); }} className="w-full">
                  Back
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Ban className="h-5 w-5" /> Ban User</DialogTitle>
            <DialogDescription>This will prevent the user from logging in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Banning <span className="text-red-500">*</span></Label>
              <Textarea 
                placeholder="Enter reason for ban (required)..." 
                value={banReason} 
                onChange={(e) => setBanReason(e.target.value)} 
                className="min-h-[80px]" 
              />
              <p className="text-xs text-gray-500">This reason will be logged for auditing purposes.</p>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowBanDialog(false)}>Cancel</Button>
              <Button onClick={handleBanUser} disabled={banning || !banReason.trim()} className="bg-red-600 hover:bg-red-700">
                {banning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
                Ban User
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unban User Dialog */}
      <Dialog open={showUnbanDialog} onOpenChange={setShowUnbanDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600"><UserCheck className="h-5 w-5" /> Unban User</DialogTitle>
            <DialogDescription>This will allow the user to log in again.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Unbanning <span className="text-red-500">*</span></Label>
              <Textarea 
                placeholder="Enter reason for unban (required)..." 
                value={unbanReason} 
                onChange={(e) => setUnbanReason(e.target.value)} 
                className="min-h-[80px]" 
              />
              <p className="text-xs text-gray-500">This reason will be logged for auditing purposes.</p>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowUnbanDialog(false)}>Cancel</Button>
              <Button onClick={handleUnbanUser} disabled={unbanning || !unbanReason.trim()} className="bg-blue-600 hover:bg-blue-700">
                {unbanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
                Unban User
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600"><Crown className="h-5 w-5" /> Admin Upgrade</DialogTitle>
            <DialogDescription>Upgrade this user's subscription with a reason code.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subscription Type</Label>
              <Select value={upgradeForm.subscription_type} onValueChange={(v) => {
                const prices = { monthly: 9.99, annual: 99, lifetime: 267 };
                setUpgradeForm(prev => ({ ...prev, subscription_type: v, price: prices[v] }));
              }}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly Pro ($9.99/mo)</SelectItem>
                  <SelectItem value="annual">Annual Pro ($99/yr)</SelectItem>
                  <SelectItem value="lifetime">Lifetime Pro ($267)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Price (USD) <span className="text-red-500">*</span></Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  type="number" 
                  step="0.01"
                  min="0"
                  placeholder="0.00" 
                  value={upgradeForm.price} 
                  onChange={(e) => setUpgradeForm(prev => ({ ...prev, price: e.target.value }))} 
                  className="pl-9 rounded-xl"
                />
              </div>
              <p className="text-xs text-gray-500">This amount will be recorded in financials. Set to 0 for free upgrades.</p>
            </div>

            <div className="space-y-2">
              <Label>Reason for Upgrade <span className="text-red-500">*</span></Label>
              <Select value={upgradeForm.reason || ''} onValueChange={(v) => setUpgradeForm(prev => ({ ...prev, reason: v, notes: v !== 'other' ? '' : prev.notes }))}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select a reason code..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_service">Customer Service Gesture</SelectItem>
                  <SelectItem value="promotional">Promotional Offer</SelectItem>
                  <SelectItem value="beta_tester">Beta Tester Reward</SelectItem>
                  <SelectItem value="referral_bonus">Referral Bonus</SelectItem>
                  <SelectItem value="bug_compensation">Bug Compensation</SelectItem>
                  <SelectItem value="vip_partner">VIP/Partner Account</SelectItem>
                  <SelectItem value="manual_payment">Manual Payment Processed</SelectItem>
                  <SelectItem value="plan_change">Plan Change/Adjustment</SelectItem>
                  <SelectItem value="other">Other (see notes)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">This will be logged in the user's activity history.</p>
            </div>

            {/* Notes field - shown when "other" is selected */}
            {upgradeForm.reason === 'other' && (
              <div className="space-y-2">
                <Label>Notes <span className="text-red-500">*</span></Label>
                <Textarea 
                  placeholder="Enter detailed notes for this upgrade (required)..." 
                  value={upgradeForm.notes} 
                  onChange={(e) => setUpgradeForm(prev => ({ ...prev, notes: e.target.value }))} 
                  className="min-h-[80px]" 
                />
                <p className="text-xs text-gray-500">Please provide details about why this upgrade is being performed.</p>
              </div>
            )}

            <DialogFooter className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleAdminUpgrade} 
                disabled={isLoading || !upgradeForm.reason.trim() || (upgradeForm.reason === 'other' && !upgradeForm.notes.trim())} 
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
                Upgrade User
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={showUserDetail} onOpenChange={setShowUserDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><User className="h-5 w-5" /> User Details</DialogTitle>
          </DialogHeader>
          {userDetailLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" /></div>
          ) : selectedUser ? (
            <div className="space-y-6">
              {/* User Header */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xl font-semibold">
                  {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{selectedUser.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getSubscriptionBadge(selectedUser)}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">${selectedUser.total_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-500">Total Revenue</p>
                </div>
              </div>

              {/* Admin Actions */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-700">Actions:</span>
                <div className="flex items-center gap-2 flex-1">
                  {selectedUser.banned ? (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => openUnbanDialog(selectedUser.id)} 
                      className="h-8 text-xs"
                    >
                      <UserCheck className="h-3 w-3 mr-1" /> Unban User
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => { setBanUserId(selectedUser.id); setShowBanDialog(true); }} 
                      className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Ban className="h-3 w-3 mr-1" /> Ban User
                    </Button>
                  )}
                  {selectedUser.account_type !== 'pro' && !selectedUser.banned && (
                    <Button 
                      size="sm" 
                      onClick={() => openUpgradeDialog(selectedUser.id)} 
                      className="h-8 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Crown className="h-3 w-3 mr-1" /> Upgrade to Pro
                    </Button>
                  )}
                  {selectedUser.account_type === 'pro' && !selectedUser.banned && (
                    <Button 
                      size="sm" 
                      onClick={() => openUpgradeDialog(selectedUser.id)} 
                      className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Crown className="h-3 w-3 mr-1" /> Change Plan
                    </Button>
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Joined</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Last Login</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedUser.last_login)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Upgraded</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedUser.upgraded_at)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Cancelled</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedUser.churned_at)}</p>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <Users className="h-6 w-6 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{selectedUser.kids_count || 0}</p>
                  <p className="text-xs text-gray-500">Kids</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl text-center">
                  <Activity className="h-6 w-6 mx-auto text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{selectedUser.activities_count || 0}</p>
                  <p className="text-xs text-gray-500">Activities</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <Calendar className="h-6 w-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{selectedUser.events_count || 0}</p>
                  <p className="text-xs text-gray-500">Events</p>
                </div>
              </div>

              {/* Payments */}
              {selectedUser.payments?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payments</h4>
                  <div className="space-y-2">
                    {selectedUser.payments.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">${payment.amount?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{payment.subscription_type}</p>
                        </div>
                        <p className="text-sm text-gray-500">{formatDateTime(payment.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Login IPs */}
              {selectedUser.ip_history?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><Globe className="h-4 w-4" /> Login IPs</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.ip_history.map((ip, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-mono text-sm text-gray-900">{ip.ip_address}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[300px]">{ip.user_agent}</p>
                        </div>
                        <p className="text-xs text-gray-500">{formatDateTime(ip.last_used)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Log */}
              {selectedUser.activity_logs?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><History className="h-4 w-4" /> Activity Log</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.activity_logs.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          {log.action === 'login' && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {log.action === 'banned' && <Ban className="h-4 w-4 text-red-500" />}
                          {log.action === 'unbanned' && <UserCheck className="h-4 w-4 text-blue-500" />}
                          {log.action === 'admin_upgraded' && <Crown className="h-4 w-4 text-purple-500" />}
                          {log.action === 'upgraded' && <Crown className="h-4 w-4 text-green-500" />}
                          <span className="text-sm text-gray-900 capitalize">{log.action.replace('_', ' ')}</span>
                          {log.details?.reason && <span className="text-xs text-gray-500 ml-1">- {log.details.reason}</span>}
                          {log.details?.subscription_type && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded ml-1">
                              {log.details.subscription_type}
                            </span>
                          )}
                          {log.details?.price !== undefined && log.details?.price > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded ml-1">
                              ${log.details.price}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 whitespace-nowrap ml-2">{formatDateTime(log.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_kidplay-hub/artifacts/7zuwi818_jgzjhpdo_image.png"
              alt="SmartDaddy AI"
              className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>
          <div className="flex items-center gap-4">
            {mfaEnabled ? (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <Shield className="h-3 w-3" /> MFA Enabled
                </span>
                <Button variant="ghost" size="sm" onClick={handleDisableMfa} className="text-gray-500 hover:text-red-600 text-xs">
                  Disable
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setShowMfaSetupDialog(true)} className="text-gray-500 hover:text-gray-700">
                <KeyRound className="h-4 w-4 mr-2" />Enable MFA
              </Button>
            )}
            <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700">Go to App</Link>
            <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-full"><LogOut className="h-4 w-4 mr-2" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* User Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><Users className="h-5 w-5 text-blue-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{stats.total}</p><p className="text-xs text-gray-500">Total Users</p></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center"><User className="h-5 w-5 text-gray-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{stats.free}</p><p className="text-xs text-gray-500">Free</p></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center"><Crown className="h-5 w-5 text-purple-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{stats.pro_monthly || 0}</p><p className="text-xs text-gray-500">Monthly</p></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center"><Calendar className="h-5 w-5 text-indigo-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{stats.pro_annual || 0}</p><p className="text-xs text-gray-500">Annual</p></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><Infinity className="h-5 w-5 text-amber-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{stats.pro_lifetime || 0}</p><p className="text-xs text-gray-500">Lifetime</p></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center"><Zap className="h-5 w-5 text-green-600" /></div>
              <div><p className="text-xl font-bold text-gray-900">{stats.pro_total || 0}</p><p className="text-xs text-gray-500">Total Pro</p></div>
            </div>
          </div>
        </div>

        {/* Revenue & Churn Cards - Cleaner Design */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <span className="text-xs text-gray-400 font-medium uppercase">Total Revenue</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">${stats.total_revenue?.toLocaleString() || '0'}</p>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-xs text-gray-400 font-medium uppercase">MRR</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">${stats.mrr?.toLocaleString() || '0'}</p>
            <p className="text-xs text-gray-500 mt-1">Monthly Recurring</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <PiggyBank className="h-5 w-5 text-purple-600" />
              </div>
              <span className="text-xs text-gray-400 font-medium uppercase">ARR</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">${stats.arr?.toLocaleString() || '0'}</p>
            <p className="text-xs text-gray-500 mt-1">Annual Recurring</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <UserMinus className="h-5 w-5 text-red-600" />
              </div>
              <span className="text-xs text-gray-400 font-medium uppercase">Churn</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.churn_rate || 0}%</p>
            <p className="text-xs text-gray-500 mt-1">{stats.churned_users || 0} users cancelled</p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="space-y-6 mb-8">
          {/* Date Range Filter */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Date Range:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 7, label: '7 Days' },
                  { value: 30, label: '30 Days' },
                  { value: 60, label: '60 Days' },
                  { value: 90, label: '90 Days' },
                  { value: 180, label: '180 Days' },
                  { value: 365, label: '1 Year' }
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setChartDateRange(option.value);
                      setIsCustomRange(false);
                      setCustomRangeLabel('');
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                      chartDateRange === option.value && !isCustomRange
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
                
                {/* Custom Date Range */}
                <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
                  <PopoverTrigger asChild>
                    <button
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-all flex items-center gap-1.5 ${
                        isCustomRange
                          ? 'bg-purple-600 border-purple-600 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Calendar className="h-3.5 w-3.5" />
                      {isCustomRange && customRangeLabel ? customRangeLabel : 'Custom'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-sm">Custom Date Range</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-500">Start Date</Label>
                          <Input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            max={customEndDate || new Date().toISOString().split('T')[0]}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-gray-500">End Date</Label>
                          <Input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            min={customStartDate}
                            max={new Date().toISOString().split('T')[0]}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setShowCustomDatePicker(false);
                            setCustomStartDate('');
                            setCustomEndDate('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                          disabled={!customStartDate || !customEndDate}
                          onClick={() => {
                            if (customStartDate && customEndDate) {
                              const start = new Date(customStartDate);
                              const end = new Date(customEndDate);
                              const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                              
                              if (diffDays < 1) {
                                toast.error('End date must be after start date');
                                return;
                              }
                              if (diffDays > 730) {
                                toast.error('Date range cannot exceed 2 years');
                                return;
                              }
                              
                              setChartDateRange(diffDays);
                              setIsCustomRange(true);
                              
                              // Format label
                              const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              setCustomRangeLabel(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
                              
                              setShowCustomDatePicker(false);
                              toast.success(`Showing data for ${diffDays} days`);
                            }
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 text-center">Max range: 2 years</p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-gray-400">
                  Showing by {chartGranularity === 'day' ? 'day' : chartGranularity === 'week' ? 'week' : 'month'}
                </span>
                {loadingCharts && <Loader2 className="h-4 w-4 animate-spin text-purple-500" />}
              </div>
            </div>
          </div>

          {/* First Row: Pie Chart + User Growth */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart - User Distribution */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <PieChartIcon className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">User Distribution</h3>
              </div>
              {stats.user_distribution && stats.user_distribution.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.user_distribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {stats.user_distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [`${value} users`, name]}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value, entry) => (
                          <span className="text-sm text-gray-700">{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  <p className="text-sm">No user data available</p>
                </div>
              )}
            </div>

            {/* Line Chart - User Growth by Type */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">User Growth</h3>
              </div>
              {userGrowthData && userGrowthData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={userGrowthData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        interval={chartGranularity === 'day' ? 0 : 'preserveStartEnd'}
                        angle={chartGranularity === 'day' ? -45 : 0}
                        textAnchor={chartGranularity === 'day' ? 'end' : 'middle'}
                        height={chartGranularity === 'day' ? 60 : 30}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                        labelStyle={{ fontWeight: 600, color: '#374151' }}
                      />
                      <Line type="monotone" dataKey="free" stroke="#9CA3AF" strokeWidth={2} name="Free" dot={false} />
                      <Line type="monotone" dataKey="pro_monthly" stroke="#8B5CF6" strokeWidth={2} name="Pro Monthly" dot={false} />
                      <Line type="monotone" dataKey="pro_annual" stroke="#F59E0B" strokeWidth={2} name="Pro Annual" dot={false} />
                      <Line type="monotone" dataKey="pro_lifetime" stroke="#10B981" strokeWidth={2} name="Pro Lifetime" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  {loadingCharts ? <Loader2 className="h-6 w-6 animate-spin" /> : <p className="text-sm">No growth data available</p>}
                </div>
              )}
              <div className="flex items-center justify-center gap-4 mt-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                  <span className="text-xs text-gray-500">Free</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-xs text-gray-500">Pro Monthly</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-xs text-gray-500">Pro Annual</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-500">Pro Lifetime</span>
                </div>
              </div>
            </div>
          </div>

          {/* Second Row: MRR Trend + Churn */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Line Chart - MRR History */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <LineChartIcon className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">MRR Trend (Last 12 Months)</h3>
              </div>
              {mrrHistory && mrrHistory.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={mrrHistory} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'mrr') return [`$${value.toFixed(2)}`, 'MRR'];
                          if (name === 'revenue') return [`$${value.toFixed(2)}`, 'Revenue'];
                          return [value, name];
                        }}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                        labelStyle={{ fontWeight: 600, color: '#374151' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="mrr" 
                        stroke="#8B5CF6" 
                        strokeWidth={2}
                        fill="url(#mrrGradient)" 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        dot={{ fill: '#10B981', strokeWidth: 0, r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  <p className="text-sm">No MRR history available</p>
                </div>
              )}
              <div className="flex items-center justify-center gap-6 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-xs text-gray-500">MRR</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-500">Monthly Revenue</span>
                </div>
              </div>
            </div>

            {/* Bar Chart - Churn */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <UserMinus className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Churn & Growth</h3>
              </div>
              {churnData && churnData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={churnData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="label" 
                        tick={{ fontSize: 10, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        interval={chartGranularity === 'day' ? 0 : 'preserveStartEnd'}
                        angle={chartGranularity === 'day' ? -45 : 0}
                        textAnchor={chartGranularity === 'day' ? 'end' : 'middle'}
                        height={chartGranularity === 'day' ? 60 : 30}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                        labelStyle={{ fontWeight: 600, color: '#374151' }}
                      />
                      <ReferenceLine y={0} stroke="#e5e7eb" />
                      <Bar dataKey="new_users" fill="#10B981" name="New Users" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="churned" fill="#EF4444" name="Churned" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  {loadingCharts ? <Loader2 className="h-6 w-6 animate-spin" /> : <p className="text-sm">No churn data available</p>}
                </div>
              )}
              <div className="flex items-center justify-center gap-6 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs text-gray-500">New Users</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs text-gray-500">Churned</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
                <span className="text-sm text-gray-500">({filteredUsers.length} total)</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => { loadUsers(); loadStats(); }}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
                
                {/* Filter Popover */}
                <Popover open={showFilterPopover} onOpenChange={setShowFilterPopover}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-10">
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="ml-2 bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="end">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm">Filters</h4>
                        {activeFilterCount > 0 && (
                          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs text-gray-500">
                            Clear all
                          </Button>
                        )}
                      </div>
                      
                      {/* Account Type Filter */}
                      <div>
                        <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Account Type</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {['free', 'pro'].map(type => (
                            <button
                              key={type}
                              onClick={() => toggleFilter('accountTypes', type)}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                filters.accountTypes.includes(type)
                                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {type === 'free' ? 'Free' : 'Pro'}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Subscription Type Filter */}
                      <div>
                        <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {['free', 'monthly', 'annual', 'lifetime', 'legacy'].map(type => (
                            <button
                              key={type}
                              onClick={() => toggleFilter('subscriptionTypes', type)}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                filters.subscriptionTypes.includes(type)
                                  ? 'bg-purple-100 border-purple-300 text-purple-700'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Status Filter */}
                      <div>
                        <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {['active', 'banned', 'churned'].map(status => (
                            <button
                              key={status}
                              onClick={() => toggleFilter('statuses', status)}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                filters.statuses.includes(status)
                                  ? status === 'banned' ? 'bg-red-100 border-red-300 text-red-700'
                                  : status === 'churned' ? 'bg-orange-100 border-orange-300 text-orange-700'
                                  : 'bg-green-100 border-green-300 text-green-700'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Group By Dropdown */}
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="w-40 h-10">
                    <Layers className="h-4 w-4 mr-2 text-gray-400" />
                    <SelectValue placeholder="Group by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No grouping</SelectItem>
                    <SelectItem value="account_type">Account Type</SelectItem>
                    <SelectItem value="subscription_type">Subscription</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input placeholder="Search users..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="pl-10 h-10 rounded-full w-64" />
                </div>
              </div>
            </div>

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-500 mr-1">Active filters:</span>
                {filters.accountTypes.map(type => (
                  <span key={type} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                    <X className="h-3 w-3 cursor-pointer hover:text-purple-900" onClick={() => toggleFilter('accountTypes', type)} />
                  </span>
                ))}
                {filters.subscriptionTypes.map(type => (
                  <span key={type} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                    <X className="h-3 w-3 cursor-pointer hover:text-blue-900" onClick={() => toggleFilter('subscriptionTypes', type)} />
                  </span>
                ))}
                {filters.statuses.map(status => (
                  <span key={status} className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                    status === 'banned' ? 'bg-red-100 text-red-700' 
                    : status === 'churned' ? 'bg-orange-100 text-orange-700'
                    : 'bg-green-100 text-green-700'
                  }`}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => toggleFilter('statuses', status)} />
                  </span>
                ))}
              </div>
            )}
          </div>

          {loadingUsers ? (
            <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" /><p className="text-sm text-gray-500 mt-2">Loading users...</p></div>
          ) : groupBy !== 'none' && groupedUsers ? (
            /* Grouped View */
            <div className="divide-y divide-gray-100">
              {Object.entries(groupedUsers).map(([groupName, groupUsers]) => (
                <div key={groupName} className="border-b border-gray-100 last:border-b-0">
                  <div className="px-6 py-3 bg-gray-50 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-gray-400" />
                    <span className="font-semibold text-gray-700">{groupName}</span>
                    <span className="text-sm text-gray-500">({groupUsers.length})</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <tbody className="divide-y divide-gray-100">
                        {groupUsers.slice(0, 10).map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => loadUserDetail(user.id)}>
                            <td className="px-6 py-4 w-1/4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{user.name || 'Unknown'}</p>
                                  <p className="text-sm text-gray-500">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">{getSubscriptionBadge(user)}</td>
                            <td className="px-6 py-4">
                              {user.total_revenue > 0 ? (
                                <span className="inline-flex items-center gap-1 text-green-600 font-semibold"><DollarSign className="h-3 w-3" />{user.total_revenue?.toFixed(2)}</span>
                              ) : (
                                <span className="text-gray-400">$0.00</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                            <td className="px-6 py-4">
                              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); loadUserDetail(user.id); }} className="h-8 text-xs">
                                <Eye className="h-3 w-3 mr-1" /> View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {groupUsers.length > 10 && (
                      <div className="px-6 py-2 text-center text-sm text-gray-500 bg-gray-50">
                        +{groupUsers.length - 10} more users. Use filters to narrow down.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Regular Table View with Pagination */
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                        <div className="flex items-center gap-1">User <SortIcon field="name" /></div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('subscription_type')}>
                        <div className="flex items-center gap-1">Subscription <SortIcon field="subscription_type" /></div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('total_revenue')}>
                        <div className="flex items-center gap-1">Revenue <SortIcon field="total_revenue" /></div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('upgraded_at')}>
                        <div className="flex items-center gap-1">Upgraded <SortIcon field="upgraded_at" /></div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('churned_at')}>
                        <div className="flex items-center gap-1">Cancelled <SortIcon field="churned_at" /></div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('created_at')}>
                        <div className="flex items-center gap-1">Joined <SortIcon field="created_at" /></div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => loadUserDetail(user.id)}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                              {user.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{user.name || 'Unknown'}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">{getSubscriptionBadge(user)}</td>
                        <td className="px-6 py-4">
                          {user.total_revenue > 0 ? (
                            <span className="inline-flex items-center gap-1 text-green-600 font-semibold"><DollarSign className="h-3 w-3" />{user.total_revenue?.toFixed(2)}</span>
                          ) : (
                            <span className="text-gray-400">$0.00</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{user.account_type === 'pro' ? formatDate(user.upgraded_at) : '-'}</td>
                        <td className="px-6 py-4 text-sm">
                          {user.churned_at ? (
                            <span className="text-red-600">{formatDate(user.churned_at)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                        <td className="px-6 py-4">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={(e) => { e.stopPropagation(); loadUserDetail(user.id); }} 
                            className="h-8 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" /> View User
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {paginatedUsers.length === 0 && (
                  <div className="p-12 text-center"><Users className="h-12 w-12 mx-auto text-gray-300" /><p className="text-sm text-gray-500 mt-2">No users found</p></div>
                )}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredUsers.length)} of {filteredUsers.length} users
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <ChevronLeft className="h-4 w-4 -ml-2" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    {getPageNumbers().map(page => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={`h-8 w-8 p-0 ${currentPage === page ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                      >
                        {page}
                      </Button>
                    ))}
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                      <ChevronRight className="h-4 w-4 -ml-2" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
